import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, Download, Sparkles } from "lucide-react";
import { theme, card, emptyFrame, btnPrimary, type as text } from "../theme";
import { DimensionHeading, SheetCard, StatusTag, type StatusTone } from "./Drawing";
import { api, apiBlob, apiErrorMessage, ApiError } from "../api/client";
import { saveBlob, fileNameSafe } from "../lib/download";
import { fmtDateOnly } from "../lib/dateOnly";
import { fmtDate, fmtDateTime } from "../lib/format";
import { MarkdownDocument } from "./MarkdownDocument";
import { NextSteps } from "./NextSteps";
import { TierLockModal, TIER_NAMES, type TierErrorPayload } from "./TierLockModal";
import { usePortalNavigate } from "../hooks/usePortalNavigate";
import { TechnicalScopeForm, type ScopeFields, type ScopeOptions } from "./TechnicalScopeForm";
import { Field, KeyValue, ModalHeader, ModalOverlay, input, textarea, secondaryBtn, dangerBtn } from "../pages/resilience/shared";

// One technical scope (docs/TECHNICAL-SCOPES.md): its details, the document (drafted by the AI from
// the organisation's own records, or edited by hand — every save is a new version), the IT
// review of the current version, and issue / withdraw. Issued and withdrawn scopes are the record:
// read-only here, and the API refuses any change to them.

export type ScopeStatus = "draft" | "drafting" | "in_review" | "changes_requested" | "confirmed" | "issued" | "withdrawn";

export const SCOPE_STATUS_LABEL: Record<ScopeStatus, string> = {
  draft: "Draft",
  drafting: "Being drafted",
  in_review: "With IT for review",
  changes_requested: "Changes requested",
  confirmed: "Confirmed by IT",
  issued: "Issued",
  withdrawn: "Withdrawn",
};

const SCOPE_STATUS_TONE: Record<ScopeStatus, StatusTone> = {
  draft: "none", drafting: "none", in_review: "partial", changes_requested: "gap", confirmed: "met", issued: "met", withdrawn: "none",
};

export function ScopeStatusTag({ status, label }: { status: string; label?: string }) {
  const s = status as ScopeStatus;
  return <StatusTag tone={SCOPE_STATUS_TONE[s] ?? "none"}>{label ?? SCOPE_STATUS_LABEL[s] ?? status}</StatusTag>;
}

const REVIEW_LABEL: Record<string, { label: string; tone: StatusTone }> = {
  pending: { label: "Waiting", tone: "partial" },
  confirmed: { label: "Confirmed", tone: "met" },
  changes_requested: { label: "Changes requested", tone: "gap" },
  withdrawn: { label: "Withdrawn", tone: "none" },
};

interface ScopeRecord extends ScopeFields {
  id: string;
  displayId: number;
  ref: string;
  workTypeLabel: string;
  status: ScopeStatus;
  currentVersion: number;
  issuedAt: string | null;
  updatedAt: string;
  draftError: string | null;
  supplier: { id: string; name: string } | null;
}

interface ScopeVersion { id: string; version: number; origin: "ai" | "edited"; createdAt: string; createdBy: string | null }

interface ScopeReview {
  id: string;
  version: number;
  reviewerUserId: string;
  reviewer: string;
  requestedBy: string;
  status: "pending" | "confirmed" | "changes_requested" | "withdrawn";
  note: string | null;
  selfReviewed: boolean;
  requestedAt: string;
  answeredAt: string | null;
}

interface ScopeDetailData {
  scope: ScopeRecord;
  document: { markdown: string; origin: "ai" | "edited"; createdAt: string } | null;
  versions: ScopeVersion[];
  reviews: ScopeReview[];
}

interface Reviewer { id: string; name: string; email: string; role: string | null; suggested: boolean }

const isLocked = (s: ScopeStatus) => s === "issued" || s === "withdrawn";

export function TechnicalScopeDetail({ scopeId, options, canEdit, onBack, onChanged }: {
  scopeId: string;
  options: ScopeOptions | null;
  canEdit: boolean;
  onBack: () => void;
  /** Called after any change so the list behind it refreshes. */
  onChanged: () => void;
}) {
  const navigate = usePortalNavigate();
  const [data, setData] = useState<ScopeDetailData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [tierError, setTierError] = useState<TierErrorPayload | null>(null);
  const [editingDetails, setEditingDetails] = useState(false);
  const [editingDoc, setEditingDoc] = useState(false);
  const [docText, setDocText] = useState("");
  const [reviewOpen, setReviewOpen] = useState(false);

  const load = useCallback(async () => {
    try { setData(await api.get<ScopeDetailData>(`/technical-scopes/${scopeId}`)); setError(null); }
    catch (e) { setError(apiErrorMessage(e, "Could not load the scope")); }
  }, [scopeId]);
  useEffect(() => { setData(null); void load(); }, [load]);

  // Poll every 3 s while the AI is drafting. A draft still "drafting" after 30 minutes was lost
  // (the server lets it be started again), so the page stops waiting on it.
  const drafting = data?.scope.status === "drafting"
    && (!data.scope.updatedAt || Date.now() - new Date(data.scope.updatedAt).getTime() < 30 * 60 * 1000);
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    if (!drafting) return;
    const t = window.setInterval(() => { void loadRef.current(); }, 3000);
    return () => window.clearInterval(t);
  }, [drafting]);
  // Tell the list once a draft finishes.
  const wasDrafting = useRef(false);
  useEffect(() => {
    if (wasDrafting.current && !drafting) onChanged();
    wasDrafting.current = !!drafting;
  }, [drafting, onChanged]);

  async function act(key: string, fn: () => Promise<unknown>, done?: string) {
    setBusy(key); setError(null); setNotice(null);
    try {
      await fn();
      if (done) setNotice(done);
      await load();
      onChanged();
    } catch (e) {
      if (e instanceof ApiError && e.status === 402 && e.code === "tier_locked") setTierError(e.payload as TierErrorPayload);
      else setError(apiErrorMessage(e, "That did not work"));
    } finally {
      setBusy(null);
    }
  }

  async function download(version?: number) {
    if (!data) return;
    setError(null);
    const v = version ?? data.scope.currentVersion;
    try { saveBlob(await apiBlob(`/technical-scopes/${scopeId}/download`, { version: String(v) }), `${fileNameSafe(`${data.scope.ref} ${data.scope.title} v${v}`)}.docx`); }
    catch { setError("Could not download the Word document"); }
  }

  if (!data) {
    return (
      <div>
        <button type="button" style={secondaryBtn} onClick={onBack}><ArrowLeft size={13} style={{ verticalAlign: -2, marginRight: 6 }} />All scopes</button>
        <div style={{ ...text.body, marginTop: 16, color: error ? theme.color.dangerText : theme.color.subtle }}>{error ?? "Loading…"}</div>
      </div>
    );
  }

  const { scope, document: doc, versions, reviews } = data;
  const locked = isLocked(scope.status);
  const writable = canEdit && !locked && !drafting;
  const current = reviews.filter((r) => r.version === scope.currentVersion && r.status !== "withdrawn");
  const confirmed = current.filter((r) => r.status === "confirmed").length;
  const canIssue = writable && scope.currentVersion > 0 && current.length > 0 && confirmed === current.length;
  const siteName = new Map((options?.sites ?? []).map((s) => [s.id, s.name]));
  const zoneName = new Map((options?.zones ?? []).map((z) => [z.id, z.name]));
  const assetName = new Map((options?.assets ?? []).map((a) => [a.id, a.name]));
  const fwName = new Map((options?.frameworks ?? []).map((f) => [f.id, f.label]));
  const names = (ids: string[], m: Map<string, string>) => ids.length ? ids.map((id) => m.get(id) ?? "Removed record").join(", ") : null;

  return (
    <div style={{ display: "grid", gap: 18 }}>
      {tierError && (
        <TierLockModal
          featureName="Technical scopes"
          requiredTierName={TIER_NAMES[tierError.requiredLevel] ?? `tier ${tierError.requiredLevel}`}
          requiredTierLevel={tierError.requiredLevel}
          currentTierLevel={tierError.currentLevel}
          onClose={() => setTierError(null)}
          onRequestUpgrade={() => navigate("/my-services")}
        />
      )}

      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <button type="button" style={{ ...secondaryBtn, marginBottom: 10 }} onClick={onBack}><ArrowLeft size={13} style={{ verticalAlign: -2, marginRight: 6 }} />All scopes</button>
          <div style={{ ...text.sectionTitle, fontSize: 18, margin: 0 }}>
            <span style={{ ...text.ref, fontSize: 14, color: theme.color.ref, marginRight: 8 }}>{scope.ref}</span>{scope.title}
          </div>
          <div style={{ ...text.hint, marginTop: 4, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <ScopeStatusTag status={scope.status} />
            <span>{scope.workTypeLabel}</span>
            {scope.currentVersion > 0 && <span>· version {scope.currentVersion}</span>}
            {scope.issuedAt && <span>· issued {fmtDate(scope.issuedAt)}</span>}
          </div>
        </div>
        {canEdit && !locked && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {canIssue && (
              <button type="button" disabled={busy !== null} style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}
                onClick={() => { if (window.confirm("Issue this version? An issued scope is the record of what was agreed and cannot be changed.")) void act("issue", () => api.post(`/technical-scopes/${scopeId}/issue`), "Issued."); }}>
                Issue
              </button>
            )}
            <button type="button" disabled={busy !== null || drafting} style={dangerBtn}
              onClick={() => { if (window.confirm("Withdraw this scope? The work will not go ahead on it. It is kept for the record and cannot be changed afterwards.")) void act("withdraw", () => api.post(`/technical-scopes/${scopeId}/withdraw`), "Withdrawn."); }}>
              Withdraw
            </button>
          </div>
        )}
      </div>

      {error && <div style={{ ...card, padding: 12, ...text.body, color: theme.color.dangerText }}>{error}</div>}
      {notice && <div style={{ ...card, padding: 12, ...text.body, color: theme.color.successText }}>{notice}</div>}
      {locked && (
        <div style={{ ...card, padding: 12, ...text.hint }}>
          {scope.status === "issued" ? "This scope has been issued. It is the record of what was agreed and can no longer be changed." : "This scope was withdrawn. It is kept for the record and can no longer be changed."}
        </div>
      )}

      {/* What a scope of work needs before it is issued: what, who, when, where, what is in and out, and IT's confirmation. */}
      {!locked && (
        <NextSteps
          items={[
            { done: !!scope.purpose?.trim(), label: "Say what the work is for" },
            { done: !!(scope.supplierId || scope.performer?.trim()), label: "Name who carries it out" },
            { done: !!scope.plannedStart && !!scope.plannedEnd, label: "Set the planned start and end" },
            { done: scope.assetIds.length + scope.zoneIds.length + scope.siteIds.length > 0, label: "Say which assets, zones or sites it touches" },
            { done: !!scope.inclusions?.trim(), label: "List what is in scope" },
            { done: !!scope.exclusions?.trim(), label: "List what is out of scope" },
            { done: !!scope.constraints?.trim(), label: "Record the constraints", hint: "Change windows, systems not to touch, who to call if something breaks." },
            { done: scope.currentVersion > 0, label: "Draft the scope document" },
            { done: current.length > 0 && confirmed === current.length, label: "Have IT confirm the current version", urgent: scope.status === "changes_requested" },
          ]}
          action={writable && options ? <button type="button" style={secondaryBtn} onClick={() => setEditingDetails(true)}>Fill these in</button> : undefined}
        />
      )}

      {/* Details */}
      <SheetCard label="Details" padding={editingDetails ? 0 : 16} actions={writable && options && !editingDetails ? <button type="button" style={secondaryBtn} onClick={() => setEditingDetails(true)}>Edit details</button> : undefined}>
        {editingDetails && options ? (
          <TechnicalScopeForm options={options} initial={scope} scopeId={scope.id}
            onSaved={() => { setEditingDetails(false); void load(); onChanged(); }}
            onCancel={() => setEditingDetails(false)} />
        ) : (
          <KeyValue items={[
            ["What the work is for", <span style={{ whiteSpace: "pre-wrap" }}>{scope.purpose}</span>],
            ["Carried out by", scope.supplier?.name ?? scope.performer],
            ["Planned", scope.plannedStart || scope.plannedEnd ? `${scope.plannedStart ? fmtDateOnly(scope.plannedStart) : "?"} to ${scope.plannedEnd ? fmtDateOnly(scope.plannedEnd) : "?"}` : null],
            ["Standards", names(scope.frameworkIds, fwName)],
            ["Sites", names(scope.siteIds, siteName)],
            ["Network zones", names(scope.zoneIds, zoneName)],
            ["Assets", names(scope.assetIds, assetName)],
            ["In scope", scope.inclusions ? <span style={{ whiteSpace: "pre-wrap" }}>{scope.inclusions}</span> : null],
            ["Out of scope", scope.exclusions ? <span style={{ whiteSpace: "pre-wrap" }}>{scope.exclusions}</span> : null],
            ["Constraints", scope.constraints ? <span style={{ whiteSpace: "pre-wrap" }}>{scope.constraints}</span> : null],
          ]} />
        )}
      </SheetCard>

      {/* Document */}
      <SheetCard
        featured
        label="Scope document"
        reference={scope.currentVersion > 0 ? `v${scope.currentVersion}${doc ? ` · ${doc.origin === "ai" ? "AI draft" : "edited"} · ${fmtDate(doc.createdAt)}` : ""}` : undefined}
        actions={
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {doc && !editingDoc && <button type="button" style={secondaryBtn} onClick={() => download()}><Download size={13} style={{ verticalAlign: -2, marginRight: 6 }} />Download Word</button>}
            {writable && !editingDoc && (
              <button type="button" disabled={busy !== null} style={secondaryBtn}
                onClick={() => {
                  if (doc && !window.confirm("Redraft the document with the AI? The new draft becomes a new version, which IT will need to review again.")) return;
                  void act("draft", () => api.post(`/technical-scopes/${scopeId}/draft`), "The AI is drafting the document. This page refreshes when it is done.");
                }}>
                <Sparkles size={13} style={{ verticalAlign: -2, marginRight: 6 }} />{doc ? "Redraft" : "Draft with AI"}
              </button>
            )}
            {writable && !editingDoc && (
              <button type="button" style={secondaryBtn} onClick={() => { setDocText(doc?.markdown ?? ""); setEditingDoc(true); }}>{doc ? "Edit" : "Write it yourself"}</button>
            )}
          </div>
        }
      >
        {drafting && (
          <div style={{ ...text.body, color: theme.color.subtle, marginBottom: doc ? 12 : 0 }}>
            The AI is drafting the document from your records. This usually takes a minute or two.
          </div>
        )}
        {scope.draftError && !drafting && (
          <div style={{ ...text.body, color: theme.color.dangerText, marginBottom: 12 }}>The last draft failed: {scope.draftError}</div>
        )}
        {editingDoc ? (
          <div style={{ display: "grid", gap: 10 }}>
            <div style={text.hint}>Saving makes a new version. Reviews asked for on the current version are withdrawn, so send the new one to IT again.</div>
            <textarea rows={24} value={docText} onChange={(e) => setDocText(e.target.value)} style={{ ...textarea, fontFamily: text.mono.fontFamily, fontSize: 12 }} />
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" disabled={busy !== null || docText.trim().length < 20} style={{ ...btnPrimary, opacity: busy || docText.trim().length < 20 ? 0.6 : 1 }}
                onClick={() => void act("save-doc", async () => { await api.put(`/technical-scopes/${scopeId}/document`, { markdown: docText }); setEditingDoc(false); }, "Saved as a new version.")}>
                {busy === "save-doc" ? "Saving…" : "Save as a new version"}
              </button>
              <button type="button" style={secondaryBtn} onClick={() => setEditingDoc(false)}>Cancel</button>
            </div>
          </div>
        ) : doc ? (
          <div style={{ maxHeight: 640, overflowY: "auto" }}>
            <MarkdownDocument content={doc.markdown} />
          </div>
        ) : !drafting && (
          <div style={{ ...emptyFrame, ...text.hint }}>
            No document yet. {writable ? "Draft it with the AI from your records, or write it yourself." : ""}
          </div>
        )}
      </SheetCard>

      {/* Review */}
      <div>
        <DimensionHeading count={current.length ? `${confirmed} of ${current.length} confirmed` : undefined}>IT review</DimensionHeading>
        <div style={{ ...text.hint, marginBottom: 10, maxWidth: 760 }}>
          The people you name confirm the technical detail of the current version on their My Tasks page, or ask for
          changes. Once every reviewer of the current version has confirmed it, you can issue it.
        </div>
        {writable && scope.currentVersion > 0 && (
          <button type="button" style={{ ...btnPrimary, marginBottom: 12 }} onClick={() => setReviewOpen(true)}>Send to IT for review</button>
        )}
        {reviews.length === 0 ? (
          <div style={{ ...emptyFrame, ...text.hint }}>Nobody has been asked to review this scope yet.</div>
        ) : (
          <div style={{ ...card, padding: 0 }}>
            {reviews.map((r, i) => {
              const s = REVIEW_LABEL[r.status] ?? { label: r.status, tone: "none" as StatusTone };
              return (
                <div key={r.id} style={{ padding: "10px 14px", borderTop: i ? `1px solid ${theme.color.border}` : "none", opacity: r.version === scope.currentVersion ? 1 : 0.65 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                    <div style={text.body}>
                      <strong>{r.reviewer}</strong>
                      <span style={{ ...text.hint, marginLeft: 8 }}>version {r.version} · asked by {r.requestedBy} on {fmtDate(r.requestedAt)}{r.selfReviewed ? " · self-reviewed" : ""}</span>
                    </div>
                    <StatusTag tone={s.tone}>{s.label}{r.answeredAt ? ` ${fmtDate(r.answeredAt)}` : ""}</StatusTag>
                  </div>
                  {r.note && <div style={{ ...text.hint, marginTop: 4, whiteSpace: "pre-wrap" }}>{r.note}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Versions */}
      {versions.length > 0 && (
        <div>
          <DimensionHeading count={versions.length}>Versions</DimensionHeading>
          <div style={{ ...card, padding: 0 }}>
            {versions.map((v, i) => (
              <div key={v.id} style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap", padding: "8px 14px", borderTop: i ? `1px solid ${theme.color.border}` : "none" }}>
                <div style={text.body}>
                  <span style={{ ...text.ref, marginRight: 8 }}>v{v.version}</span>
                  {v.origin === "ai" ? "Drafted by the AI" : "Edited"}{v.createdBy ? ` · ${v.createdBy}` : ""}
                  <span style={{ ...text.hint, marginLeft: 8 }}>{fmtDateTime(v.createdAt)}</span>
                  {v.version === scope.currentVersion && <span style={{ ...text.meta, marginLeft: 8 }}>current</span>}
                </div>
                <button type="button" style={secondaryBtn} onClick={() => download(v.version)}><Download size={13} style={{ verticalAlign: -2, marginRight: 6 }} />Word</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {reviewOpen && (
        <ReviewRequest
          scopeId={scopeId}
          version={scope.currentVersion}
          already={new Set(current.map((r) => r.reviewerUserId))}
          onClose={() => setReviewOpen(false)}
          onSent={(n) => { setReviewOpen(false); setNotice(n ? `Sent to ${n} reviewer${n === 1 ? "" : "s"}. They are emailed and answer on My Tasks.` : "Those people were already asked."); void load(); onChanged(); }}
        />
      )}
    </div>
  );
}

// ── Send to IT for review ────────────────────────────────────────────────────

function ReviewRequest({ scopeId, version, already, onClose, onSent }: {
  scopeId: string;
  version: number;
  already: Set<string>;
  onClose: () => void;
  onSent: (requested: number) => void;
}) {
  const [reviewers, setReviewers] = useState<Reviewer[] | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [note, setNote] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ reviewers: Reviewer[] }>("/technical-scopes/reviewers")
      .then((d) => setReviewers([...d.reviewers].sort((a, b) => Number(b.suggested) - Number(a.suggested))))
      .catch((e) => setError(apiErrorMessage(e, "Could not load the people who can review")));
  }, []);

  const needle = q.trim().toLowerCase();
  const shown = (reviewers ?? []).filter((r) => !needle || r.name.toLowerCase().includes(needle) || r.email.toLowerCase().includes(needle) || (r.role ?? "").toLowerCase().includes(needle));

  async function send() {
    if (!chosen.size) return;
    setBusy(true); setError(null);
    try {
      const r = await api.post<{ requested: number }>(`/technical-scopes/${scopeId}/reviews`, { reviewerIds: [...chosen], ...(note.trim() ? { note: note.trim() } : {}) });
      onSent(r.requested);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not send the review request"));
      setBusy(false);
    }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={`Send version ${version} to IT for review`} onClose={onClose} />
      <div style={{ padding: 20, display: "grid", gap: 12 }}>
        <div style={text.hint}>
          Choose up to ten people who can check the technical detail. Your IT people are listed first and marked suggested.
          Each is emailed and answers on their My Tasks page.
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people" style={input} />
        <div style={{ maxHeight: 300, overflowY: "auto", border: `1px solid ${theme.color.border}`, borderRadius: 3 }}>
          {reviewers === null && !error && <div style={{ ...text.hint, padding: 10 }}>Loading…</div>}
          {reviewers && shown.length === 0 && <div style={{ ...text.hint, padding: 10 }}>Nobody matches.</div>}
          {shown.map((r) => {
            const asked = already.has(r.id);
            const on = chosen.has(r.id);
            return (
              <label key={r.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 10px", borderBottom: `1px solid ${theme.color.border}`, cursor: asked ? "default" : "pointer", opacity: asked ? 0.6 : 1 }}>
                <input type="checkbox" disabled={asked || (!on && chosen.size >= 10)} checked={on || asked}
                  onChange={(e) => setChosen((prev) => { const n = new Set(prev); if (e.target.checked) n.add(r.id); else n.delete(r.id); return n; })}
                  style={{ marginTop: 3 }} />
                <span style={{ minWidth: 0 }}>
                  <span style={{ ...text.body, color: theme.color.heading }}>{r.name}</span>
                  {r.suggested && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, padding: "0 6px", borderRadius: 2, border: `1px solid ${theme.color.accent}`, color: theme.color.accentText }}>Suggested</span>}
                  {asked && <span style={{ ...text.meta, marginLeft: 8 }}>already asked</span>}
                  <span style={{ ...text.meta, display: "block" }}>{r.email}{r.role ? ` · ${r.role}` : ""}</span>
                </span>
              </label>
            );
          })}
        </div>
        <Field label="Note to the reviewers">
          <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} style={textarea} placeholder="Optional: anything they should look at first" />
        </Field>
        {error && <div style={{ ...text.body, color: theme.color.dangerText }}>{error}</div>}
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" disabled={busy || chosen.size === 0} style={{ ...btnPrimary, opacity: busy || chosen.size === 0 ? 0.6 : 1 }} onClick={send}>
            {busy ? "Sending…" : chosen.size ? `Send to ${chosen.size} reviewer${chosen.size === 1 ? "" : "s"}` : "Send"}
          </button>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </ModalOverlay>
  );
}
